import React, { useRef, useState, useEffect } from 'react';
import styles from './ChatInterface.module.css';

const defaultRecommendations = [
  { id: 1, text: 'Follow up with new leads from last week.' },
  { id: 2, text: 'Review product content for EcoWear.' },
  { id: 3, text: 'Schedule a performance email for GreenThreads.' },
];

function ChatInterface({ messages = [], onSend, suggestedQuestions = [] }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [recommendations, setRecommendations] = useState(defaultRecommendations);
  const [banner, setBanner] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    // Scroll to the bottom when messages change
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = (msg) => {
    const message = typeof msg === 'string' ? msg : input;
    if (!message.trim()) {
      setError('Message cannot be empty');
      return;
    }
    setError('');
    if (onSend) onSend(message);
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  function handleAccept(rec) {
    setBanner(`"${rec.text}" added to the CRM todo list!`);
    setRecommendations(recommendations.filter(r => r.id !== rec.id));
    setTimeout(() => setBanner(null), 3500);
  }
  function handleDiscard(rec) {
    setRecommendations(recommendations.filter(r => r.id !== rec.id));
  }

  return (
    <div className={styles.chatContainer} style={{height:'100%', minHeight:0, display:'flex', flexDirection:'column'}}>
      {banner && (
        <div style={{background:'#00bfae',color:'#fff',padding:'1rem 1.5rem',borderRadius:10,margin:'1rem',fontWeight:600,boxShadow:'0 2px 8px rgba(0,191,174,0.13)',textAlign:'center',zIndex:10}}>
          {banner}
        </div>
      )}
      {recommendations.length > 0 && (
        <div style={{padding:'1.2rem 1.1rem 0.5rem 1.1rem',background:'#f7f7f7',borderBottom:'1px solid #e6f4f1'}}>
          <div style={{fontWeight:700,color:'#004443',marginBottom:'0.7rem'}}>Recommended Actions</div>
          <ul style={{listStyle:'none',padding:0,margin:0}}>
            {recommendations.map(rec => (
              <li key={rec.id} style={{marginBottom:'0.7rem',display:'flex',alignItems:'center',gap:'0.7rem'}}>
                <span style={{flex:1}}>{rec.text}</span>
                <button onClick={()=>handleAccept(rec)} style={{background:'#00bfae',color:'#fff',border:'none',borderRadius:6,padding:'0.4rem 1.1rem',fontWeight:600,cursor:'pointer'}}>Accept</button>
                <button onClick={()=>handleDiscard(rec)} style={{background:'#e6f4f1',color:'#004443',border:'none',borderRadius:6,padding:'0.4rem 1.1rem',fontWeight:600,cursor:'pointer'}}>Discard</button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.chatHistory} style={{flex:1, minHeight:0, overflowY:'auto',padding:'1rem',display:'flex',flexDirection:'column',gap:'0.5rem',background:'#f7f7f7'}}>
        {messages.map((msg, idx) => {
          if (typeof msg === 'string') {
            // fallback for string messages
            return <div key={idx} className={styles.message}>{msg}</div>;
          }
          const isUser = msg.user !== undefined;
          const text = msg.user || msg.AI;
          return (
            <div
              key={idx}
              className={
                isUser
                  ? `${styles.message} ${styles.userMessage}`
                  : `${styles.message} ${styles.aiMessage}`
              }
              style={isUser ? { alignSelf: 'flex-end' } : { alignSelf: 'flex-start' }}
            >
              {text}
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>
      {suggestedQuestions.length > 0 && (
        <div className={styles.suggestedQuestionsBar}>
          {suggestedQuestions.map((q, i) => (
            <button
              key={i}
              className={styles.suggestedQuestionBtn}
              onClick={() => handleSend(q)}
              type="button"
            >
              {q}
            </button>
          ))}
        </div>
      )}
      <div className={styles.inputBar} style={{borderTop:'1px solid #e6f4f1', background:'#fff'}}>
        <input
          className={styles.input}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your message..."
        />
        <button
          className={styles.voiceButton}
          title="Voice Support (coming soon)"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="9" y="3" width="6" height="12" rx="3" fill="#00bfae" />
            <rect x="11" y="17" width="2" height="4" rx="1" fill="#00bfae" />
            <path d="M5 12V13C5 16.3137 7.68629 19 11 19H13C16.3137 19 19 16.3137 19 13V12" stroke="#00bfae" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          className={styles.imageButton}
          title="Image Support (coming soon)"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="5" width="18" height="14" rx="3" fill="#e6f4f1" stroke="#00bfae" strokeWidth="2" />
            <circle cx="8" cy="10" r="2" fill="#00bfae" />
            <path d="M21 19L16 13L11 18L7 14L3 19" stroke="#00bfae" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          className={styles.sendButton}
          onClick={() => handleSend()}
          disabled={!input.trim()}
        >
          Send
        </button>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

export default ChatInterface;
